export interface CompletedUploadPart {
    partNumber: number;
    etag: string;
}

/**
 * Slices `file` to match the server's part boundaries and PUTs each part
 * directly to its presigned URL (S3 multipart upload), collecting the ETag
 * each PUT returns. `partUrls[i]` is part `i + 1`.
 */
export async function uploadVideoParts(
    file: File,
    partUrls: readonly string[],
    partSizeBytes: number,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
    /**
     * Aborts the transfer. Parts go one at a time, so this stops the one in flight and the loop
     * never reaches the next — without it, closing the upload dialog left every remaining part
     * still uploading, and the upload quietly finished with nothing on screen to show it.
     */
    signal?: AbortSignal,
): Promise<CompletedUploadPart[]> {
    const parts: CompletedUploadPart[] = [];
    let uploadedBytes = 0;

    for (const [
        i,
        partUrl,
    ] of partUrls.entries()) {
        signal?.throwIfAborted();
        const start = i * partSizeBytes;
        const end = Math.min(start + partSizeBytes, file.size);
        const chunk = file.slice(start, end);

        const res = await fetch(partUrl, { method: "PUT", body: chunk, signal });
        if (!res.ok) throw new Error(`Upload failed on part ${i + 1} (${res.status})`);
        const etag = res.headers.get("ETag");
        if (!etag) throw new Error(`Upload response for part ${i + 1} is missing an ETag`);
        parts.push({ partNumber: i + 1, etag });

        uploadedBytes += chunk.size;
        onProgress?.(uploadedBytes, file.size);
    }

    return parts;
}

/** What `createVideoUpload` hands back — the asset row plus where to send each part. */
export interface StartedVideoUpload {
    assetId: string;
    uploadId: string;
    partUrls: readonly string[];
    partSizeBytes: number;
}

/**
 * The three server calls an upload makes, supplied by the caller so this stays free of Apollo —
 * the single-file dialog and the bulk queue each bind them to their own mutations.
 */
export interface VideoUploadCalls {
    create: (file: File) => Promise<StartedVideoUpload>;
    complete: (input: { assetId: string; uploadId: string; parts: CompletedUploadPart[] }) => Promise<unknown>;
    discard: (assetId: string) => Promise<unknown>;
}

/**
 * One whole upload: create the asset, PUT every part, complete it. Resolves with the asset id once
 * the bytes are in — processing happens afterwards, on the server.
 *
 * On any failure, abort included, the half-made asset is deleted before the error is rethrown. The
 * row exists from the moment `create` returns, before a single byte has moved, and left behind it
 * lists as UPLOADING with nothing behind it until the twelve-hour abandonment sweep. A failed
 * `complete` is ambiguous — the server may have finished while the response was lost — and is
 * deleted anyway: the operator has just been told it failed and will upload again, so keeping the
 * row buys a duplicate rather than a rescue. A failed delete is swallowed; the server reaps a FAILED
 * asset on read, so this is the fast path, not the only one.
 */
export async function runVideoUpload(
    file: File,
    calls: VideoUploadCalls,
    options: {
        onProgress?: (fraction: number) => void;
        /** Called as soon as the asset row exists, before any bytes move. */
        onCreated?: (assetId: string) => void;
        signal?: AbortSignal;
    } = {},
): Promise<string> {
    let assetId: string | null = null;
    try {
        const started = await calls.create(file);
        assetId = started.assetId;
        options.onCreated?.(assetId);
        const parts = await uploadVideoParts(
            file,
            started.partUrls,
            started.partSizeBytes,
            (uploaded, total) => options.onProgress?.(uploaded / total),
            options.signal,
        );
        await calls.complete({ assetId, uploadId: started.uploadId, parts });
        return assetId;
    } catch (e) {
        if (assetId) await calls.discard(assetId).catch(() => {});
        throw e;
    }
}
